/**
 * Integrations Tab - Connected services and MCP servers
 *
 * Features:
 * - List of connected integrations (Formitable, Zenchef, OpenTable, TheFork, etc.)
 * - Add/Edit integration modals
 * - MCP server configuration
 * - Empty state with action button
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Plus, Plug } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { useIntegrationForm } from '@/hooks/api/useIntegrations'
import { usePermissions } from '@/hooks/api'
import type { Integration, MCPServerConfigFrontend, Language } from '@/types/integrations'
import { integrationDetails, integrableToolNames } from '@/lib/integrations/integrationDetails'
import { getLocalizedText } from '@/lib/integrations/integrationUtils'
import {
  IntegrationCard,
  AddIntegrationModal,
  EditIntegrationModal,
  MCPServerCard,
  EditMCPServerModal,
} from '../integrations'
export function IntegrationsTab() {
  const { t } = useTranslation()
  const { canManage: isAdmin } = usePermissions()
  const { id } = useParams<{ id: string }>()
  const assistantId = id ? parseInt(id, 10) : undefined

  // Form context and integration hook
  const {
    addIntegration,
    updateIntegration,
    deleteIntegration,
    getExistingIntegrations,
    getMcpServers,
    addMcpServer,
    updateMcpServer,
    deleteMcpServer,
    isValidating,
  } = useIntegrationForm()

  // Watch config for reactive updates
  const config = useWatch({ name: 'config' }) || {}

  // Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [integrationToEdit, setIntegrationToEdit] = useState<Integration | null>(null)
  const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null)
  const [mcpToEdit, setMcpToEdit] = useState<{ server: MCPServerConfigFrontend; index: number } | null>(null)
  const [mcpToDelete, setMcpToDelete] = useState<{ server: MCPServerConfigFrontend; index: number; name?: string } | null>(null)

  // Get language from i18n - map to integration language format
  const { i18n } = useTranslation()
  const language: Language = i18n.language.startsWith('nl') ? 'nl-NL' : 'en'

  // Get integrations from config
  const integrations = (config?.tools || []).filter(
    (tool: Integration) => tool && tool.name && integrableToolNames.includes(tool.name)
  )
  const mcpServers = getMcpServers()

  // Handlers
  const handleAddIntegration = async (newIntegration: { name: string; config?: Record<string, any> }) => {
    const success = await addIntegration(newIntegration)
    if (success) {
      setIsAddModalOpen(false)
    }
    return success
  }

  const handleSaveEdit = async (updatedIntegration: Integration) => {
    const success = await updateIntegration(updatedIntegration)
    if (success) {
      setIntegrationToEdit(null)
    }
    return success
  }

  const handleConfirmDelete = () => {
    if (!integrationToDelete) return
    deleteIntegration(integrationToDelete.name)
    setIntegrationToDelete(null)
  }

  const handleAddMcpServer = async (entry: MCPServerConfigFrontend) => {
    const success = await addMcpServer(entry)
    if (success) {
      setIsAddModalOpen(false)
    }
    return success
  }

  const handleSaveMcpEdit = async (entry: MCPServerConfigFrontend, index: number) => {
    const success = await updateMcpServer({ index }, entry)
    if (success) {
      setMcpToEdit(null)
    }
    return success
  }

  const handleConfirmMcpDelete = () => {
    if (mcpToDelete === null) return
    deleteMcpServer({ index: mcpToDelete.index })
    setMcpToDelete(null)
  }

  return (
    <div className="space-y-6">
      {/* Integrations Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.assistants.integrations.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.integrations.description')}
          </p>
        </div>
        <Button type="button" color="secondary" onPress={() => setIsAddModalOpen(true)} iconLeading={Plus}>
          {t('resources.assistants.integrations.add_integration')}
        </Button>
      </div>

      {/* Integration List */}
      {integrations.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center dark:border-gray-600 dark:bg-gray-800/50">
          <Plug className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-base font-medium text-gray-900 dark:text-gray-100">
            {t('resources.assistants.integrations.no_integrations')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.integrations.no_integrations_description')}
          </p>
          <Button
            type="button"
            color="secondary"
            onPress={() => setIsAddModalOpen(true)}
            className="mt-4"
            iconLeading={Plus}
          >
            {t('resources.assistants.integrations.add_first_integration')}
          </Button>
        </div>
      ) : (
        <div>
          {integrations.map((integration: Integration, index: number) => (
            <IntegrationCard
              key={`${integration.name}-${index}`}
              integration={integration}
              language={language}
              onEdit={setIntegrationToEdit}
              onDelete={setIntegrationToDelete}
            />
          ))}
        </div>
      )}

      {/* MCP Servers Section */}
      {mcpServers.length > 0 && (
        <div className="mt-8">
          <div className="mb-4 flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.assistants.integrations.mcp_servers')}
            </h3>
            <span className="rounded-full border border-amber-500 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
              {t('resources.generic.beta')}
            </span>
          </div>
          {mcpServers.map((server: MCPServerConfigFrontend, idx: number) => (
            <MCPServerCard
              key={idx}
              server={server}
              index={idx}
              language={language}
              onEdit={(s, i) => setMcpToEdit({ server: s, index: i })}
              onDelete={(i, name) => setMcpToDelete({ server, index: i, name })}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <AddIntegrationModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddIntegration}
        onSaveMcp={handleAddMcpServer}
        existingIntegrations={getExistingIntegrations()}
        language={language}
        isValidating={isValidating}
        isAdmin={isAdmin}
        assistantId={assistantId}
      />

      <EditIntegrationModal
        open={!!integrationToEdit}
        onClose={() => setIntegrationToEdit(null)}
        onSave={handleSaveEdit}
        integration={integrationToEdit}
        language={language}
        isValidating={isValidating}
        isAdmin={isAdmin}
      />

      <EditMCPServerModal
        open={!!mcpToEdit}
        onClose={() => setMcpToEdit(null)}
        onSave={handleSaveMcpEdit}
        server={mcpToEdit?.server || null}
        index={mcpToEdit?.index || 0}
        language={language}
      />

      {/* Delete Confirmations */}
      <AlertDialog
        isOpen={!!integrationToDelete}
        onClose={() => setIntegrationToDelete(null)}
        onConfirm={handleConfirmDelete}
        title={t('resources.assistants.integrations.delete_integration')}
        description={t('resources.assistants.integrations.delete_integration_message', {
          name: integrationToDelete ? getLocalizedText(integrationDetails[integrationToDelete.name]?.displayName, language) : ''
        })}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
      />

      <AlertDialog
        isOpen={!!mcpToDelete}
        onClose={() => setMcpToDelete(null)}
        onConfirm={handleConfirmMcpDelete}
        title={t('resources.assistants.integrations.delete_mcp_server')}
        description={t('resources.assistants.integrations.delete_mcp_server_message', {
          name: mcpToDelete?.name || mcpToDelete?.server?.alias || t('resources.assistants.integrations.mcp_server')
        })}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
      />
    </div>
  )
}
